import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { Alert } from "@/elements/components/ui/alert";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { api } from "@/elements/components/utils/convex-api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { ConvexError } from "convex/values";
import { useLocalSearchParams, useRouter } from "expo-router";
import { addMinutes, format } from "date-fns";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import RequestSentModal from "@/elements/components/modals/request-sent-modal";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import {
  LocationData,
  LocationSheetPayload,
  LocationSheetResult,
  toBookingLocation,
} from "@/elements/components/sheets/location-sheet";
import goBack from "@/elements/components/utils/go-back";

function LessonRequestScreen() {
  const [modalVisible, setModalVisible] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const { childId, tutorId, timeSlot, introCall, subjectId, note, lessonMode, duration, recurring, recurringSlots } =
    useLocalSearchParams<{
      childId: string;
      tutorId: string;
      timeSlot: string;
      introCall: string;
      subjectId: string;
      note: string;
      lessonMode: string;
      duration: string;
      recurring?: string;
      recurringSlots?: string;
    }>();

  const isRecurring = recurring === "true";
  const parsedRecurringSlots: number[] = useMemo(() => {
    if (!recurringSlots) return [];
    try {
      return JSON.parse(decodeURIComponent(recurringSlots));
    } catch {
      return [];
    }
  }, [recurringSlots]);
  const { data: parent } = useQuery({
    ...convexQuery(api.parent.home.queries.getProfile, {}),
  });
  const parentId = parent?._id;
  const { data: child } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getStudent, {
      studentId: childId as Id<"students">,
    }),
    enabled: !!childId,
  });
  const yearGroupId = child?.yearGroup?._id;
  const keyStageId = child?.keyStage?._id;
  const curriculumId = child?.curriculum?._id;
  const router = useRouter();
  const durationMinutes = introCall ? 15 : parseInt(duration || "60");
  const { data: pricingBreakdown } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getTotalCreditsNeeded, {
      keyStageId: keyStageId as Id<"keyStages">,
      durationMinutes: durationMinutes as 15 | 60 | 90 | 120,
      deliveryMethod: lessonMode === "inPerson" ? "in_person" : "remote",
      subjectId: subjectId as Id<"subjects">,
      tutorId: tutorId as Id<"tutorProfiles">,
      studentId: childId as Id<"students">,
    }),
    enabled: !!keyStageId && !!lessonMode && (!!duration || !!introCall) && !introCall && !!tutorId && !!childId && !!subjectId,
  });

  const creditsPerLesson = pricingBreakdown?.totalCreditsNeeded || 0;
  const baseCreditsNeeded = pricingBreakdown?.baseCreditsNeeded || 0;
  const inPersonSurcharge = pricingBreakdown?.inPersonSurcharge || 0;
  const lessonCount = isRecurring ? parsedRecurringSlots.length : 1;
  const totalCreditsNeeded = introCall ? 0 : creditsPerLesson * lessonCount;
  const finalCreditsNeeded = totalCreditsNeeded;
  const { data: tutorStudentSubjects = [] } = useQuery(
    convexQuery(
      api.parent.bookLesson.queries.getTutorStudentSubjects,
      tutorId && childId
        ? {
            tutorId: tutorId as Id<"tutorProfiles">,
            studentId: childId as Id<"students">,
          }
        : "skip",
    ),
  );
  const {
    mutateAsync: createBookingRequest,
    isPending: isCreatingBookingRequest,
    isSuccess: isBookingRequestCreated,
  } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.bookLesson.mutations.createBookingRequest
    ),
  });

  const {
    mutateAsync: createRecurringBookingRequest,
    isPending: isCreatingRecurringBookingRequest,
    isSuccess: isRecurringBookingRequestCreated,
  } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.bookLesson.mutations.createRecurringBookingRequest
    ),
  });

  const isCreating = isCreatingBookingRequest || isCreatingRecurringBookingRequest;
  const isCreated = isBookingRequestCreated || isRecurringBookingRequestCreated;
  const { data: availableCredits = 0 } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getParentAvailableCredits, {}),
  });
  const selectedSubjectLabel = useMemo(() => {
    return subjectId
      ? tutorStudentSubjects.find((subject) => subject._id === subjectId)?.label
      : "";
  }, [subjectId, tutorStudentSubjects]);
  const [location, setLocation] = useState<LocationData | null>(null);

  const { open } = useSheet<LocationSheetPayload, LocationSheetResult>(
    SheetIds.LOCATION
  );

  const isBalanceSufficient = availableCredits >= finalCreditsNeeded;

  const onSubmit = useCallback(async () => {
    if (!childId || !tutorId || !timeSlot) {
      return;
    }
    try {
      setBookingError(null);
      if (isRecurring && parsedRecurringSlots.length > 0) {
        const slots = parsedRecurringSlots.map((startTime) => ({
          startTime,
          endTime: addMinutes(new Date(startTime), durationMinutes).getTime(),
        }));

        await createRecurringBookingRequest({
          tutorId: tutorId as Id<"tutorProfiles">,
          slots,
          subjectId: subjectId as Id<"subjects">,
          notes: note,
          yearGroupId: yearGroupId as Id<"yearGroups">,
          keyStageId: keyStageId as Id<"keyStages">,
          curriculumId: curriculumId as Id<"curriculum">,
          studentId: childId as Id<"students">,
          totalCreditsPerSlot: creditsPerLesson,
          deliveryMethod: lessonMode === "inPerson" ? "in_person" : "remote",
          location:
            lessonMode === "inPerson" && !!location
              ? toBookingLocation(location)
              : undefined,
        });
      } else {
        await createBookingRequest({
          tutorId: tutorId as Id<"tutorProfiles">,
          startTime: Number(timeSlot),
          endTime: addMinutes(new Date(Number(timeSlot)), durationMinutes).getTime(),
          type: introCall ? "intro_call" : "regular_session",
          subjectId: subjectId as Id<"subjects">,
          notes: note,
          yearGroupId: yearGroupId as Id<"yearGroups">,
          keyStageId: keyStageId as Id<"keyStages">,
          curriculumId: curriculumId as Id<"curriculum">,
          studentId: childId as Id<"students">,
          totalCredits: creditsPerLesson,
          deliveryMethod: lessonMode === "inPerson" ? "in_person" : "remote",
          location:
            lessonMode === "inPerson" && !!location
              ? toBookingLocation(location)
              : undefined,
        });
      }
      setModalVisible(true);
    } catch (error) {
      console.error(error);
      const message =
        error instanceof ConvexError
          ? String(error.data)
          : "Failed to send booking request";
      setBookingError(message);
    }
  }, [
    childId,
    tutorId,
    timeSlot,
    introCall,
    subjectId,
    note,
    createBookingRequest,
    createRecurringBookingRequest,
    yearGroupId,
    keyStageId,
    curriculumId,
    creditsPerLesson,
    lessonMode,
    location,
    isRecurring,
    parsedRecurringSlots,
    durationMinutes,
  ]);

  const onBuyCredits = useCallback(() => {
    if (!parentId) {
      return;
    }
    
    const bookingData = {
      childId,
      tutorId,
      timeSlot,
      introCall,
      subjectId,
      note,
      lessonMode,
      duration,
      location,
    };
    
    const encodedBookingData = encodeURIComponent(JSON.stringify(bookingData));
    router.push(`/(app)/buy-credits?returnTo=booking&bookingData=${encodedBookingData}`);
  }, [parentId, router, childId, tutorId, timeSlot, introCall, subjectId, note, lessonMode, duration, location]);

  const handleLocationSheet = useCallback(async () => {
    const result = await open({
      childId: childId as Id<"students">,
      tutorId: tutorId as Id<"tutorProfiles">,
    });
    if (result?.location) {
      setLocation(result.location);
    }
  }, [open, childId, tutorId]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase gap-6">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back </AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="gap-8">
              <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-dark">
                Book a {introCall ? "free intro" : "lesson"}
              </Text>
              <View>
                <View className="px-[11px] py-6 shadow bg-teachLoop-creamLight rounded-[11px]">
                  {note && (
                    <View className="gap-6">
                      <Text className="text-[20px] font-geologica-medium font-medium leading-[1.5] text-gray-800">
                        Summary
                      </Text>
                      <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-800">
                        “{note}”
                      </Text>
                    </View>
                  )}
                  {!!note && <View className="h-4" />}
                  <View className="flex-1 gap-2">
                    {!!note && <View className="h-[1px] bg-black opacity-10" />}
                    <View className="gap-2">
                      <View className="flex-row justify-between gap-3">
                        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                          Subject
                        </Text>
                        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                          {selectedSubjectLabel}
                        </Text>
                      </View>
                      <View className="flex-row justify-between gap-3">
                        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                          Level
                        </Text>
                        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                          {child?.keyStage?.label}, {child?.yearGroup?.label}
                        </Text>
                      </View>
                      <View className="flex-row justify-between gap-3">
                        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                          Mode
                        </Text>
                        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                          {lessonMode === "inPerson" ? "In Person" : "Remote"}
                        </Text>
                      </View>
                    </View>
                    <View className="h-[1px] bg-black opacity-10" />
                    <View className="gap-2">
                      {isRecurring && parsedRecurringSlots.length > 0 ? (
                        <View className="gap-2">
                          <View className="flex-row justify-between gap-3">
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                              Recurring lessons
                            </Text>
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                              {parsedRecurringSlots.length} lessons
                            </Text>
                          </View>
                          {parsedRecurringSlots.sort((a, b) => a - b).map((slot) => (
                            <View key={slot} className="flex-row justify-between gap-3">
                              <Text className="text-sm font-geologica-regular font-normal leading-[1.5] text-gray-500">
                                {format(new Date(slot), "EEE, dd MMM yyyy")}
                              </Text>
                              <Text className="text-sm font-geologica-regular font-normal leading-[1.5] text-gray-500 text-end">
                                {format(new Date(slot), "HH:mm")} -{" "}
                                {format(addMinutes(new Date(slot), durationMinutes), "HH:mm")}
                              </Text>
                            </View>
                          ))}
                        </View>
                      ) : (
                        <Fragment>
                          <View className="flex-row justify-between gap-3">
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                              Schedule
                            </Text>
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                              {format(new Date(Number(timeSlot)), "EEEE, MMMM do")}
                            </Text>
                          </View>
                          <View className="flex-row justify-between gap-3">
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                              Time
                            </Text>
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                              {format(new Date(Number(timeSlot)), "HH:mm")} -{" "}
                              {format(
                                addMinutes(new Date(Number(timeSlot)), durationMinutes),
                                "HH:mm"
                              )}
                            </Text>
                          </View>
                        </Fragment>
                      )}
                      {lessonMode === "inPerson" && (
                        <View className="flex-row justify-between gap-3">
                          <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                            Location{" "}
                          </Text>
                          <Text
                            className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end underline"
                            onPress={handleLocationSheet}
                            numberOfLines={1}
                          >
                            {location ? location.address : "Add Location"}
                          </Text>
                        </View>
                      )}
                    </View>
                    {!!finalCreditsNeeded && !introCall && (
                      <Fragment>
                        <View className="h-[1px] bg-black opacity-10" />
                        <View className="flex-row justify-between gap-3">
                          <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                            Credits per lesson
                          </Text>
                          <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                            {baseCreditsNeeded}
                          </Text>
                        </View>
                        {lessonMode === "inPerson" && inPersonSurcharge > 0 && (
                          <View className="flex-row justify-between gap-3">
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                              In-person lesson surcharge
                            </Text>
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                              {inPersonSurcharge}
                            </Text>
                          </View>
                        )}
                        {isRecurring && lessonCount > 1 && (
                          <View className="flex-row justify-between gap-3">
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600">
                              Number of lessons
                            </Text>
                            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                              × {lessonCount}
                            </Text>
                          </View>
                        )}
                        <View className="flex-row justify-between gap-3">
                          <Text className="text-[20px] font-geologica-regular font-normal leading-[1.5] text-gray-600">
                            Total credits needed
                          </Text>
                          <Text className="text-[20px] font-geologica-regular font-normal leading-[1.5] text-gray-600 text-end">
                            {finalCreditsNeeded}
                          </Text>
                        </View>
                      </Fragment>
                    )}
                  </View>
                </View>

                {!introCall && !isCreated && (
                  <Fragment>
                    <View className="h-6" />
                    {isBalanceSufficient ? (
                      <Alert type="success">
                        <Alert.Icon type="success" />
                        <Alert.Box>
                          <Alert.Title>
                            Your current balance is sufficient{" "}
                          </Alert.Title>
                          <Alert.SubTitle>{availableCredits}</Alert.SubTitle>
                        </Alert.Box>
                      </Alert>
                    ) : (
                      <Alert type="warning">
                        <Alert.Icon type="warning" />
                        <Alert.Box>
                          <Alert.Title>
                            Your current balance is insufficient{" "}
                          </Alert.Title>
                          <Alert.SubTitle>{availableCredits}</Alert.SubTitle>
                        </Alert.Box>
                      </Alert>
                    )}
                  </Fragment>
                )}

                <View className="h-8" />

                {bookingError && (
                  <Alert type="warning" size="md">
                    <Alert.Icon type="warning" />
                    <Alert.Box>
                      <Alert.Title>Unable to book</Alert.Title>
                      <Alert.SubTitle>{bookingError}</Alert.SubTitle>
                    </Alert.Box>
                  </Alert>
                )}

                {bookingError && <View className="h-4" />}

                {isBalanceSufficient || introCall || isCreated ? (
                  <Button
                    disabled={
                      isCreating ||
                      (lessonMode === "inPerson" && !location)
                    }
                    fullWidth
                    size="md"
                    intent="primary"
                    onPress={onSubmit}
                  >
                    <Button.Text size="md">
                      {isRecurring && lessonCount > 1
                        ? `Send booking request (${lessonCount} lessons)`
                        : "Send booking request"}
                    </Button.Text>
                  </Button>
                ) : (
                  <Button
                    disabled={!parent}
                    fullWidth
                    size="md"
                    intent="secondary"
                    onPress={onBuyCredits}
                  >
                    <Button.Text size="md">Buy credits</Button.Text>
                  </Button>
                )}
              </View>
            </View>
          </View>
        </ResponsiveContent>

        <RequestSentModal
          isVisible={modalVisible}
          onClose={() => setModalVisible(false)}
          onDismiss={() => {
            router.replace("/(app)/home");
          }}
        />
      </ScrollView>
    </View>
  );
}

export default LessonRequestScreen;
